/**
 * The rendered text scale of the document.
 *
 * A host can enlarge text without a change to the browser default font size:
 * an injected `-webkit-text-size-adjust`, a WKWebView text zoom, or the
 * Android WebView font scale. CSS cannot query any of them. The rendered
 * size of a probe can. This measures a span at a declared font size and
 * returns rendered over declared. The result is 1 at the default text size
 * under every mechanism, and the enlargement factor otherwise.
 */

/** A size large enough that a rounded height still resolves a 5% step. */
export const PROBE_FONT_SIZE_PX = 100;

export const TEXT_SCALE_PROBE_ATTRIBUTE = "data-text-scale-probe";

/**
 * Returns null before layout runs, for example in a test DOM, because a
 * zero height is not a scale.
 */
export function measureTextScale(doc: Document): number | null {
  const probe = doc.createElement("span");
  probe.setAttribute(TEXT_SCALE_PROBE_ATTRIBUTE, "");
  probe.textContent = "M";
  // A unitless line-height follows the used font size, so the box is as
  // tall as the text the fan sees. The font family does not change that, so
  // the fallback face is fine before the web fonts load.
  probe.style.cssText = [
    "position: absolute",
    "visibility: hidden",
    "pointer-events: none",
    "display: inline-block",
    `font-size: ${PROBE_FONT_SIZE_PX}px`,
    "line-height: 1",
    "padding: 0",
    "border: 0",
    "white-space: nowrap",
  ].join("; ");
  doc.body.appendChild(probe);
  let height: number;
  try {
    height = probe.getBoundingClientRect().height;
  } finally {
    probe.remove();
  }

  if (height === 0) return null;

  return Math.round((height / PROBE_FONT_SIZE_PX) * 100) / 100;
}
